import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { BadgeModule } from 'primeng/badge';
import { Subject, takeUntil } from 'rxjs';
import { AudioNotificationService } from 'src/app/shared/services/audio-notification.service';
import { ChatService } from 'src/app/shared/services/chat.service';
import {
  WebSocketService,
  WS_MESSAGE_TYPE,
} from 'src/app/shared/services/web-socket.service';
import { AppConstant } from 'src/app/shared/utilities/app.constant';
import { ChatWindowComponent } from '../chat-window/chat-window.component';

@Component({
  selector: 'app-chat-bubble',
  templateUrl: './chat-bubble.component.html',
  styleUrls: ['./chat-bubble.component.scss'],
  imports: [BadgeModule, SvgIconComponent, ChatWindowComponent],
})
export class ChatBubbleComponent implements OnInit, OnDestroy {
  totalUnreadMsg: number = 0;
  isChatWindowVisible: boolean = false;

  // Window visibility tracking for sound notifications
  private isDocumentVisible = true;
  private isWindowFocused = true;

  private destroy$ = new Subject<void>();
  private readonly handleNewMessageBound: (
    message: AppConstant.Message
  ) => void;

  constructor(
    private chatService: ChatService,
    private _io: WebSocketService,
    private audioNotificationService: AudioNotificationService
  ) {
    this.handleNewMessageBound = this.handleNewMessage.bind(this);
  }

  ngOnInit(): void {
    // Register WebSocket handler for sound notifications
    this.registerWebSocketHandlers();

    this.chatService
      .getUnreadLiveMessages()
      .pipe(takeUntil(this.destroy$))
      .subscribe(totalUnreadLiveMessages => {
        if (totalUnreadLiveMessages) {
          this.setTotalUnread(totalUnreadLiveMessages);
        }
      });
  }

  ngOnDestroy(): void {
    this._io.unregisterMessageHanlder(
      WS_MESSAGE_TYPE.USER__NEW_MESSAGE,
      this.handleNewMessageBound
    );

    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Track window focus state for notification logic
   */
  @HostListener('window:focus')
  onWindowFocus() {
    this.isWindowFocused = true;
  }

  /**
   * Track window blur state for notification logic
   */
  @HostListener('window:blur')
  onWindowBlur() {
    this.isWindowFocused = false;
  }

  /**
   * Track document visibility state for notification logic
   */
  @HostListener('document:visibilitychange')
  onVisibilityChange() {
    this.isDocumentVisible = !document.hidden;
  }

  /**
   * Register WebSocket message handlers for sound notifications
   */
  private registerWebSocketHandlers() {
    this._io.registerMessageHanlder(
      WS_MESSAGE_TYPE.USER__NEW_MESSAGE,
      this.handleNewMessageBound
    );
  }

  /**
   * Handle incoming chat messages and play sound notifications
   */
  private handleNewMessage(message: AppConstant.Message) {
    // Play audio notification for not user messages
    const isNotUserMessage =
      message && message.senderType !== AppConstant.SenderTypes.USER;

    if (isNotUserMessage) {
      if (!this.isChatWindowVisible) {
        this.setTotalUnread(this.totalUnreadMsg + 1);
      }

      // TC-001: Play sound when user NOT in window (document hidden OR window not focused OR chat window closed)
      // TC-002: Do NOT play when user IS in window (document visible AND window focused AND chat window open)
      const isUserInWindow =
        this.isDocumentVisible &&
        this.isWindowFocused &&
        this.isChatWindowVisible;

      this.audioNotificationService.playNotification({
        isUserActive: isUserInWindow,
        isSystemMessage: message.senderType === AppConstant.SenderTypes.SYSTEM,
      });
    }
  }

  setTotalUnread(totalCountUnreadMessages: number) {
    this.totalUnreadMsg = totalCountUnreadMessages;
  }

  toggleChatWindow() {
    this.isChatWindowVisible = !this.isChatWindowVisible;
  }

  onClearEvent(event: any) {
    this.toggleChatWindow();
  }
}
